import { useSeoMeta } from '@unhead/react';
import { Link } from 'react-router-dom';
import { Bell, Lock, Server } from 'lucide-react';
import { AppLayout } from '@/components/AppLayout';
import { Card, CardContent } from '@/components/ui/card';
import { Switch } from '@/components/ui/switch';
import { Slider } from '@/components/ui/slider';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { PushNotificationCard } from '@/components/PushNotificationCard';
import { useAlerts, type AlertCategory, type AlertRule } from '@/hooks/useAlerts';
import { useSubscription } from '@/hooks/useSubscription';
import { cn } from '@/lib/utils';

const CATEGORY_META: Record<AlertCategory, { label: string; icon: string }> = {
  liquidity: { label: 'Liquidity', icon: '🏛️' },
  crypto: { label: 'Crypto', icon: '🟢' },
  signal: { label: 'Signal', icon: '📡' },
};

const Alerts = () => {
  useSeoMeta({
    title: 'Alerts — BCH Radar',
    description: 'Configure custom liquidity, crypto, and signal alerts with your own thresholds.',
  });

  const { rules, toggle, setThreshold } = useAlerts();
  const { isPro } = useSubscription();

  const byCategory = (cat: AlertCategory) => rules.filter((r) => r.category === cat);

  return (
    <AppLayout>
      <div className="mx-auto max-w-2xl">
        <div className="flex items-center gap-3">
          <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary/15 text-primary">
            <Bell className="h-5 w-5" />
          </div>
          <div>
            <h1 className="text-2xl font-bold tracking-tight">Alerts</h1>
            <p className="text-sm text-muted-foreground">
              {isPro ? 'Full alert suite unlocked.' : 'Limited alerts on the free plan.'}
            </p>
          </div>
        </div>

        {!isPro && (
          <Card className="mt-4 border-primary/30 bg-primary/5">
            <CardContent className="flex items-center justify-between gap-4 p-4">
              <p className="text-sm">
                Unlock all alerts, faster delivery, and custom thresholds with
                Pro.
              </p>
              <Button asChild size="sm">
                <Link to="/pricing">Upgrade</Link>
              </Button>
            </CardContent>
          </Card>
        )}

        <Card className="mt-4 border-chart-2/30 bg-chart-2/5">
          <CardContent className="flex items-start gap-3 p-4 text-sm text-muted-foreground">
            <Server className="mt-0.5 h-4 w-4 shrink-0 text-chart-2" />
            <p>
              Alerts are evaluated server-side every ~30 minutes against fresh
              data. Enable{' '}
              <span className="text-foreground/80">push notifications</span> below
              to get alerted on this device — even when the app is closed.
            </p>
          </CardContent>
        </Card>

        <PushNotificationCard rules={rules} isPro={isPro} />

        <div className="mt-6 space-y-6">
          {(['signal', 'liquidity', 'crypto'] as AlertCategory[]).map((cat) => (
            <div key={cat}>
              <h2 className="mb-2 flex items-center gap-2 text-sm font-semibold uppercase tracking-wider text-muted-foreground">
                <span>{CATEGORY_META[cat].icon}</span>
                {CATEGORY_META[cat].label}
              </h2>
              <Card>
                <CardContent className="divide-y divide-border/60 p-0">
                  {byCategory(cat).map((rule) => (
                    <AlertRow
                      key={rule.id}
                      rule={rule}
                      locked={rule.pro && !isPro}
                      onToggle={() => toggle(rule.id)}
                      onThreshold={(v) => setThreshold(rule.id, v)}
                    />
                  ))}
                </CardContent>
              </Card>
            </div>
          ))}
        </div>
      </div>
    </AppLayout>
  );
};

function AlertRow({
  rule,
  locked,
  onToggle,
  onThreshold,
}: {
  rule: AlertRule;
  locked: boolean;
  onToggle: () => void;
  onThreshold: (v: number) => void;
}) {
  const showSlider = rule.unit !== 'phase';
  const max = rule.unit === 'pts' ? 30 : rule.unit === '%' ? 20 : 50;

  return (
    <div className={cn('p-4', locked && 'opacity-60')}>
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <span className="text-sm font-medium">{rule.label}</span>
          {rule.pro && (
            <Badge
              variant="outline"
              className="border-primary/40 text-primary text-[10px]"
            >
              PRO
            </Badge>
          )}
        </div>
        {locked ? (
          <Lock className="h-4 w-4 text-muted-foreground" />
        ) : (
          <Switch checked={rule.enabled} onCheckedChange={onToggle} />
        )}
      </div>
      {showSlider && !locked && rule.enabled && (
        <div className="mt-3 flex items-center gap-3">
          <Slider
            value={[rule.threshold]}
            min={rule.unit === 'pts' ? 2 : 0.5}
            max={max}
            step={rule.unit === '%' ? 0.5 : 1}
            onValueChange={([v]) => onThreshold(v)}
            className="flex-1"
          />
          <span className="w-16 shrink-0 text-right font-mono text-sm">
            {rule.threshold} {rule.unit}
          </span>
        </div>
      )}
    </div>
  );
}

export default Alerts;
